/**
 * CSV parsing + roster preview/import logic.
 *
 * Lives separate from the React pages so it stays unit-testable. The
 * parser is intentionally small: split by lines/commas, support quoted
 * cells, normalize headers. We only need it to handle hand-prepared
 * coach exports — not arbitrary spreadsheet exotica.
 */

import type { Id } from '../models';
import { addRosterPlayerResult, createDivision, createTeam, listDivisions, listTeams } from './rosterService';

export type CanonicalField =
  | 'division'
  | 'team'
  | 'player_name'
  | 'email'
  | 'phone'
  | 'jersey_number'
  | 'position'
  | 'captain'
  | 'alternate';

export const CANONICAL_FIELDS: CanonicalField[] = [
  'division',
  'team',
  'player_name',
  'email',
  'phone',
  'jersey_number',
  'position',
  'captain',
  'alternate',
];

/** Loose header → canonical-field map. */
const HEADER_ALIASES: Record<string, CanonicalField> = {
  division: 'division',
  div: 'division',
  pool: 'division',
  bracket: 'division',
  team: 'team',
  team_name: 'team',
  squad: 'team',
  player: 'player_name',
  player_name: 'player_name',
  name: 'player_name',
  full_name: 'player_name',
  first_last: 'player_name',
  email: 'email',
  email_address: 'email',
  phone: 'phone',
  mobile: 'phone',
  cell: 'phone',
  jersey: 'jersey_number',
  jersey_number: 'jersey_number',
  number: 'jersey_number',
  '#': 'jersey_number',
  position: 'position',
  pos: 'position',
  role: 'position',
  captain: 'captain',
  is_captain: 'captain',
  c: 'captain',
  alternate: 'alternate',
  alt: 'alternate',
  is_alternate: 'alternate',
};

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

export interface RosterPreviewRow {
  /** 1-based row number, including header offset. */
  lineNumber: number;
  raw: Record<string, string>;
  /** Canonical fields after mapping. */
  data: Partial<Record<CanonicalField, string>>;
  /** Errors that block this row from being imported. */
  errors: string[];
  /** Non-blocking notes (e.g. "matched existing player"). */
  warnings: string[];
}

export interface RosterPreview {
  rows: RosterPreviewRow[];
  /** Map of canonical → header from the file. */
  columnMapping: Partial<Record<CanonicalField, string>>;
  /** Headers from the source file, in order. */
  sourceHeaders: string[];
  /** Detected divisions (unique). */
  divisions: string[];
  /** Detected teams keyed by division. */
  teamsByDivision: Record<string, string[]>;
  importableCount: number;
  errorCount: number;
}

// --------------------------------------------------------------------------
// CSV parser (RFC 4180-lite)
// --------------------------------------------------------------------------

export function parseCsv(text: string): ParsedCsv {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        cell += c;
      }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') {
        row.push(cell);
        cell = '';
      } else if (c === '\r') {
        // Skip — handle on \n.
      } else if (c === '\n') {
        row.push(cell);
        rows.push(row);
        row = [];
        cell = '';
      } else {
        cell += c;
      }
    }
  }
  // Final cell/row.
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  // Drop fully-empty trailing rows.
  while (rows.length > 0 && rows[rows.length - 1].every((c) => c.trim() === '')) {
    rows.pop();
  }
  if (rows.length === 0) return { headers: [], rows: [] };
  const [headers, ...body] = rows;
  return { headers: headers.map((h) => h.trim()), rows: body };
}

export function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9#]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
}

export function autoMapColumns(headers: string[]): Partial<Record<CanonicalField, string>> {
  const map: Partial<Record<CanonicalField, string>> = {};
  headers.forEach((h) => {
    const norm = normalizeHeader(h);
    const canonical = HEADER_ALIASES[norm];
    if (canonical && !map[canonical]) map[canonical] = h;
  });
  return map;
}

// --------------------------------------------------------------------------
// Preview building
// --------------------------------------------------------------------------

const TRUE_VALUES = new Set(['true', 'yes', 'y', '1', 'c', 'x', 'captain']);
const truthy = (v?: string) => Boolean(v) && TRUE_VALUES.has(v!.trim().toLowerCase());

export function buildRosterPreview(
  parsed: ParsedCsv,
  mapping?: Partial<Record<CanonicalField, string>>,
): RosterPreview {
  const columnMapping = mapping ?? autoMapColumns(parsed.headers);
  const headerIndex: Partial<Record<CanonicalField, number>> = {};
  (Object.entries(columnMapping) as Array<[CanonicalField, string]>).forEach(([k, v]) => {
    const idx = parsed.headers.indexOf(v);
    if (idx >= 0) headerIndex[k] = idx;
  });

  const seenJerseysByTeam = new Map<string, Set<number>>();
  const seenPlayersByTeam = new Map<string, Set<string>>();
  const teamsByDivision: Record<string, Set<string>> = {};
  const allDivisions = new Set<string>();

  const previewRows: RosterPreviewRow[] = parsed.rows.map((cells, idx) => {
    const raw: Record<string, string> = {};
    parsed.headers.forEach((h, i) => {
      raw[h] = cells[i] ?? '';
    });
    const data: Partial<Record<CanonicalField, string>> = {};
    (Object.keys(headerIndex) as CanonicalField[]).forEach((k) => {
      const i = headerIndex[k]!;
      data[k] = (cells[i] ?? '').trim();
    });

    const errors: string[] = [];
    const warnings: string[] = [];

    if (!data.player_name) errors.push('Missing player name.');
    if (!data.team) errors.push('Missing team.');
    if (!data.jersey_number) errors.push('Missing jersey number.');

    const jerseyNum = data.jersey_number ? Number(data.jersey_number) : NaN;
    if (data.jersey_number && Number.isNaN(jerseyNum)) {
      errors.push(`Jersey "${data.jersey_number}" is not a number.`);
    }

    const teamKey = `${data.division ?? ''}|${data.team ?? ''}`.toLowerCase();
    if (data.team) {
      if (!seenJerseysByTeam.has(teamKey)) seenJerseysByTeam.set(teamKey, new Set());
      const set = seenJerseysByTeam.get(teamKey)!;
      if (!Number.isNaN(jerseyNum)) {
        if (set.has(jerseyNum)) {
          errors.push(`Duplicate jersey #${jerseyNum} on team ${data.team}.`);
        } else {
          set.add(jerseyNum);
        }
      }
    }

    if (data.team && data.player_name) {
      if (!seenPlayersByTeam.has(teamKey)) seenPlayersByTeam.set(teamKey, new Set());
      const pset = seenPlayersByTeam.get(teamKey)!;
      const pkey = data.player_name.toLowerCase();
      if (pset.has(pkey)) errors.push(`Duplicate player "${data.player_name}" on team ${data.team}.`);
      else pset.add(pkey);
    }

    if (data.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) {
      warnings.push(`Email "${data.email}" looks malformed — will save as-is.`);
    }

    if (data.division) allDivisions.add(data.division);
    if (data.division && data.team) {
      if (!teamsByDivision[data.division]) teamsByDivision[data.division] = new Set();
      teamsByDivision[data.division].add(data.team);
    }

    return {
      lineNumber: idx + 2,
      raw,
      data,
      errors,
      warnings,
    };
  });

  const importable = previewRows.filter((r) => r.errors.length === 0).length;
  const errored = previewRows.filter((r) => r.errors.length > 0).length;

  return {
    rows: previewRows,
    columnMapping,
    sourceHeaders: parsed.headers,
    divisions: Array.from(allDivisions),
    teamsByDivision: Object.fromEntries(
      Object.entries(teamsByDivision).map(([k, v]) => [k, Array.from(v)]),
    ),
    importableCount: importable,
    errorCount: errored,
  };
}

// --------------------------------------------------------------------------
// Commit
// --------------------------------------------------------------------------

export interface ImportResult {
  divisionsCreated: number;
  teamsCreated: number;
  playersAdded: number;
  rowsSkipped: number;
}

/** Write preview rows into the store. Skips rows with errors. */
export function commitRosterImport(tournamentId: Id, preview: RosterPreview): ImportResult {
  const result: ImportResult = {
    divisionsCreated: 0,
    teamsCreated: 0,
    playersAdded: 0,
    rowsSkipped: 0,
  };

  // Build/lookup divisions.
  const divLookup = new Map<string, string>(); // name → id
  listDivisions(tournamentId).forEach((d) => divLookup.set(d.name.toLowerCase(), d.id));
  preview.divisions.forEach((name) => {
    const key = name.toLowerCase();
    if (!divLookup.has(key)) {
      const d = createDivision({ tournamentId, name });
      divLookup.set(key, d.id);
      result.divisionsCreated += 1;
    }
  });
  // Default division if none provided.
  if (!divLookup.size) {
    const d = createDivision({ tournamentId, name: 'Open' });
    divLookup.set('open', d.id);
    result.divisionsCreated += 1;
  }

  // Build/lookup teams keyed by `${division}|${team}`.
  const teamLookup = new Map<string, string>();
  listTeams(tournamentId).forEach((t) => {
    const div = listDivisions(tournamentId).find((d) => d.id === t.divisionId);
    const key = `${div?.name ?? ''}|${t.name}`.toLowerCase();
    teamLookup.set(key, t.id);
  });

  preview.rows.forEach((row) => {
    if (row.errors.length > 0) {
      result.rowsSkipped += 1;
      return;
    }
    const divName = row.data.division ?? 'Open';
    const teamName = row.data.team!;
    const divId = divLookup.get(divName.toLowerCase()) ?? divLookup.values().next().value!;
    const teamKey = `${divName}|${teamName}`.toLowerCase();
    let teamId = teamLookup.get(teamKey);
    if (!teamId) {
      const team = createTeam({ tournamentId, divisionId: divId, name: teamName });
      teamId = team.id;
      teamLookup.set(teamKey, teamId);
      result.teamsCreated += 1;
    }
    const rosterResult = addRosterPlayerResult({
      tournamentId,
      teamId,
      displayName: row.data.player_name!,
      email: row.data.email,
      phone: row.data.phone,
      jerseyNumber: Number(row.data.jersey_number) || 0,
      position: row.data.position,
      isCaptain: truthy(row.data.captain),
      isAlternate: truthy(row.data.alternate),
    });
    if (rosterResult.entry) result.playersAdded += 1;
    else result.rowsSkipped += 1;
  });

  return result;
}
